<template>
  <div class="wechat-debug-container">
    <div class="debug-header">
      <h1>微信API调试工具</h1>
      <p>测试所有微信相关功能和接口</p>
      <div class="pagespy-info">
        <p class="pagespy-status">
          <span
            class="status-indicator"
            :class="{ active: pageSpyLoaded, disabled: !pageSpyEnabled }"
          ></span>
          PageSpy调试工具: {{ getPageSpyStatus() }}
        </p>
        <p v-if="pageSpyLoaded" class="pagespy-tip">
          💡 查看左下角的PageSpy图标，点击可进入远程调试模式
        </p>
        <p v-else-if="!pageSpyEnabled" class="pagespy-tip warning">
          ⚠️ PageSpy 已禁用，如需启用请设置环境变量 VITE_ENABLE_PAGESPY=true
        </p>
      </div>
    </div>

    <!-- 环境检测区域 -->
    <div class="debug-section">
      <h2>环境检测</h2>
      <div class="env-info">
        <div class="info-item">
          <label>是否微信环境:</label>
          <span :class="isWechatEnv ? 'success' : 'warning'">
            {{ isWechatEnv ? "是" : "否" }}
          </span>
        </div>
        <div class="info-item">
          <label>是否微信开发工具:</label>
          <span :class="isWechatDevtools ? 'success' : 'info'">
            {{ isWechatDevtools ? "是" : "否" }}
          </span>
        </div>
        <div class="info-item">
          <label>User Agent:</label>
          <span class="code">{{ userAgent }}</span>
        </div>
        <div class="info-item">
          <label>当前URL:</label>
          <span class="code">{{ currentUrl }}</span>
        </div>
        <div class="info-item">
          <label>签名URL:</label>
          <span class="code">{{ signatureUrl }}</span>
        </div>
      </div>
    </div>

    <!-- JS-Config API 测试 -->
    <div class="debug-section">
      <h2>JS-Config API 测试</h2>
      <div class="test-controls">
        <div class="input-group">
          <label>测试URL:</label>
          <input v-model="testUrl" type="text" placeholder="输入要测试的URL" />
        </div>
        <div class="button-group">
          <button
            @click="testJsConfig"
            :disabled="loading.jsConfig"
            class="test-btn"
          >
            {{ loading.jsConfig ? "测试中..." : "测试 JS-Config" }}
          </button>
          <button
            @click="testWechatSDKInit"
            :disabled="loading.sdkInit"
            class="test-btn"
          >
            {{
              loading.sdkInit ? "初始化中..." : "测试微信SDK初始化(Debug模式)"
            }}
          </button>
        </div>
      </div>

      <div v-if="results.jsConfig" class="result-box">
        <h3>JS-Config 结果:</h3>
        <pre>{{ JSON.stringify(results.jsConfig, null, 2) }}</pre>
      </div>

      <div v-if="results.sdkInit" class="result-box">
        <h3>微信SDK初始化结果:</h3>
        <pre>{{ JSON.stringify(results.sdkInit, null, 2) }}</pre>
      </div>

      <div v-if="errors.jsConfig" class="error-box">
        <h3>JS-Config错误信息:</h3>
        <pre>{{ errors.jsConfig }}</pre>
      </div>

      <div v-if="errors.sdkInit" class="error-box">
        <h3>SDK初始化错误信息:</h3>
        <pre>{{ errors.sdkInit }}</pre>
      </div>
    </div>

    <!-- 微信登录 API 测试 -->
    <div class="debug-section">
      <h2>微信登录 API 测试</h2>
      <div class="test-controls">
        <div class="input-group">
          <label>微信授权码:</label>
          <input
            v-model="wechatCode"
            type="text"
            placeholder="输入微信授权返回的code"
          />
        </div>
        <button
          @click="testWechatLogin"
          :disabled="loading.login"
          class="test-btn"
        >
          {{ loading.login ? "测试中..." : "测试微信登录" }}
        </button>
      </div>

      <div v-if="results.login" class="result-box">
        <h3>登录结果:</h3>
        <pre>{{ JSON.stringify(results.login, null, 2) }}</pre>
      </div>

      <div v-if="errors.login" class="error-box">
        <h3>错误信息:</h3>
        <pre>{{ errors.login }}</pre>
      </div>
    </div>

    <!-- 绑定操作测试 -->
    <div class="debug-section">
      <h2>绑定操作测试</h2>
      <div class="test-controls">
        <div class="input-group">
          <label>OpenID:</label>
          <input v-model="openid" type="text" placeholder="输入OpenID" />
        </div>
        <div class="input-group">
          <label>用户ID:</label>
          <input v-model="userid" type="text" placeholder="输入学号/工号" />
        </div>

        <div class="button-group">
          <button
            @click="testBindAccount"
            :disabled="loading.bind"
            class="test-btn"
          >
            {{ loading.bind ? "绑定中..." : "测试绑定账号" }}
          </button>
          <button
            @click="testGetBinding"
            :disabled="loading.getBinding"
            class="test-btn"
          >
            {{ loading.getBinding ? "查询中..." : "查询绑定信息" }}
          </button>
          <button
            @click="testUpdateBinding"
            :disabled="loading.updateBinding"
            class="test-btn"
          >
            {{ loading.updateBinding ? "更新中..." : "更新绑定" }}
          </button>
        </div>
      </div>

      <div v-if="results.bind" class="result-box">
        <h3>绑定结果:</h3>
        <pre>{{ JSON.stringify(results.bind, null, 2) }}</pre>
      </div>

      <div v-if="results.getBinding" class="result-box">
        <h3>绑定信息:</h3>
        <pre>{{ JSON.stringify(results.getBinding, null, 2) }}</pre>
      </div>

      <div v-if="results.updateBinding" class="result-box">
        <h3>更新结果:</h3>
        <pre>{{ JSON.stringify(results.updateBinding, null, 2) }}</pre>
      </div>

      <div v-if="errors.bind" class="error-box">
        <h3>绑定错误:</h3>
        <pre>{{ errors.bind }}</pre>
      </div>

      <div v-if="errors.getBinding" class="error-box">
        <h3>查询错误:</h3>
        <pre>{{ errors.getBinding }}</pre>
      </div>

      <div v-if="errors.updateBinding" class="error-box">
        <h3>更新错误:</h3>
        <pre>{{ errors.updateBinding }}</pre>
      </div>
    </div>

    <!-- 自动化授权测试 -->
    <div class="debug-section">
      <h2>自动化授权测试</h2>

      <!-- 授权流程状态 -->
      <div class="auth-status" v-if="authInProgress || authStep !== 'idle'">
        <div class="status-indicator-row">
          <span
            class="status-dot"
            :class="{ active: authStep !== 'idle' }"
          ></span>
          <span class="status-text">{{ getAuthStepDescription() }}</span>
        </div>
      </div>

      <div class="test-controls">
        <div class="input-group">
          <label>AppID:</label>
          <input
            v-model="authConfig.appid"
            type="text"
            placeholder="从环境变量VITE_WECHAT_APPID自动读取"
            :disabled="authInProgress"
          />
        </div>
        <div class="input-group">
          <label>回调URL:</label>
          <input
            v-model="authConfig.redirectUri"
            type="text"
            placeholder="输入回调URL"
            :disabled="authInProgress"
          />
        </div>
        <div class="input-group">
          <label>作用域:</label>
          <select v-model="authConfig.scope" :disabled="authInProgress">
            <option value="snsapi_base">snsapi_base (静默授权)</option>
            <option value="snsapi_userinfo">snsapi_userinfo (用户信息)</option>
          </select>
        </div>
        <div class="input-group">
          <label>状态参数:</label>
          <input
            v-model="authConfig.state"
            type="text"
            placeholder="自动生成唯一标识"
            :disabled="authInProgress"
          />
        </div>

        <div class="button-group">
          <button
            @click="startAutomatedAuth"
            :disabled="authInProgress"
            class="test-btn primary"
          >
            {{ authInProgress ? "授权进行中..." : "🚀 开始自动化授权测试" }}
          </button>
          <button
            @click="generateAuthUrl"
            :disabled="authInProgress"
            class="test-btn"
          >
            生成授权URL
          </button>
        </div>
      </div>

      <div v-if="generatedAuthUrl" class="result-box">
        <h3>生成的授权URL:</h3>
        <div class="url-result">
          <p class="code">{{ generatedAuthUrl }}</p>
          <button @click="copyToClipboard(generatedAuthUrl)" class="copy-btn">
            复制
          </button>
          <a :href="generatedAuthUrl" target="_blank" class="test-link"
            >测试跳转</a
          >
        </div>
      </div>

      <div class="auth-flow-help">
        <h4>📝 自动化流程说明:</h4>
        <ol>
          <li>点击"开始自动化授权测试"按钮</li>
          <li>自动生成授权URL并跳转到微信授权页面</li>
          <li>用户在微信中完成授权</li>
          <li>自动回调到当前页面并填入授权码</li>
          <li>自动执行登录测试并显示结果</li>
        </ol>
        <p class="tip">
          💡 需要配置环境变量 <code>VITE_WECHAT_APPID</code> 才能使用自动化功能
        </p>
      </div>
    </div>

    <!-- 实时日志 -->
    <div class="debug-section">
      <h2>实时日志</h2>
      <div class="history-controls">
        <button @click="clearLogs" class="clear-btn">清空日志</button>
        <button @click="printDebugInfo" class="test-btn">打印调试信息</button>
      </div>

      <div class="log-container">
        <div
          v-for="(log, index) in debugLogs"
          :key="index"
          class="log-item"
          :class="log.level"
        >
          <span class="log-time">{{ log.timestamp }}</span>
          <span class="log-level">[{{ log.level.toUpperCase() }}]</span>
          <span class="log-message">{{ log.message }}</span>
          <pre v-if="log.data" class="log-data">{{
            typeof log.data === "string"
              ? log.data
              : JSON.stringify(log.data, null, 2)
          }}</pre>
        </div>
      </div>
    </div>

    <!-- 测试历史 -->
    <div class="debug-section">
      <h2>测试历史</h2>
      <div class="history-controls">
        <button @click="clearHistory" class="clear-btn">清空历史</button>
        <button @click="exportHistory" class="export-btn">导出历史</button>
      </div>

      <div v-if="testHistory.length > 0" class="history-list">
        <div
          v-for="(item, index) in testHistory"
          :key="index"
          class="history-item"
        >
          <div class="history-header">
            <span class="timestamp">{{ item.timestamp }}</span>
            <span class="action">{{ item.action }}</span>
            <span :class="`status ${item.success ? 'success' : 'error'}`">
              {{ item.success ? "成功" : "失败" }}
            </span>
          </div>
          <div class="history-details">
            <pre>{{ JSON.stringify(item.result, null, 2) }}</pre>
          </div>
        </div>
      </div>

      <div v-else class="no-history">
        <p>暂无测试历史</p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from "vue";
import { notificationService } from "@/services/notificationService";
import {
  getWechatJsConfig,
  wechatLogin,
  bindWechatOpenid,
  getWechatBinding,
  updateWechatBinding,
  isWechatEnvironment,
  isWechatDevtools as checkWechatDevtools,
  getSignatureUrl,
  buildWechatAuthUrl,
  type WechatJsConfigResponse,
  type WechatLoginResponse,
  type WechatBindResponse,
  type WechatUserBinding,
  type WechatUpdateBindResponse,
} from "@/api/wechat";
import { wechatService } from "@/services/wechatService";

interface TestResult {
  jsConfig: WechatJsConfigResponse | null;
  login: WechatLoginResponse | null;
  bind: WechatBindResponse | null;
  getBinding: WechatUserBinding | null;
  updateBinding: WechatUpdateBindResponse | null;
  sdkInit: any | null;
}

interface LoadingState {
  jsConfig: boolean;
  login: boolean;
  bind: boolean;
  getBinding: boolean;
  updateBinding: boolean;
  sdkInit: boolean;
}

interface ErrorState {
  jsConfig: string;
  login: string;
  bind: string;
  getBinding: string;
  updateBinding: string;
  sdkInit: string;
}

interface AuthConfig {
  appid: string;
  redirectUri: string;
  scope: "snsapi_base" | "snsapi_userinfo";
  state: string;
}

interface TestHistoryItem {
  timestamp: string;
  action: string;
  success: boolean;
  result: any;
}

interface DebugLogItem {
  timestamp: string;
  level: "info" | "warn" | "error";
  message: string;
  data?: any;
}

const isWechatEnv = ref(false);
const isWechatDevtools = ref(false);
const userAgent = ref("");
const currentUrl = ref("");
const signatureUrl = ref("");

const testUrl = ref("");
const wechatCode = ref("");
const openid = ref("");
const userid = ref("");

const results = ref<TestResult>({
  jsConfig: null,
  login: null,
  bind: null,
  getBinding: null,
  updateBinding: null,
  sdkInit: null,
});

const loading = ref<LoadingState>({
  jsConfig: false,
  login: false,
  bind: false,
  getBinding: false,
  updateBinding: false,
  sdkInit: false,
});

const errors = ref<ErrorState>({
  jsConfig: "",
  login: "",
  bind: "",
  getBinding: "",
  updateBinding: "",
  sdkInit: "",
});

const authConfig = ref<AuthConfig>({
  appid: import.meta.env.VITE_WECHAT_APPID || "",
  redirectUri: "",
  scope: "snsapi_base",
  state: "",
});

const generatedAuthUrl = ref("");
const testHistory = ref<TestHistoryItem[]>([]);
const debugLogs = ref<DebugLogItem[]>([]);
const pageSpyLoaded = ref(false);
const pageSpyEnabled = ref(import.meta.env.VITE_ENABLE_PAGESPY === "true");
const authInProgress = ref(false);
const authStep = ref<
  "idle" | "generating" | "waiting" | "processing" | "completed"
>("idle");

const getPageSpyStatus = () => {
  if (!pageSpyEnabled.value) return "已禁用";
  if (pageSpyLoaded.value) return "已加载";
  return "加载中...";
};

const addLog = (
  level: "info" | "warn" | "error",
  message: string,
  data?: any,
) => {
  debugLogs.value.unshift({
    timestamp: new Date().toLocaleString(),
    level,
    message,
    data,
  });

  // 限制日志数量，避免内存泄漏
  if (debugLogs.value.length > 100) {
    debugLogs.value = debugLogs.value.slice(0, 100);
  }
};

const addToHistory = (action: string, success: boolean, result: any) => {
  testHistory.value.unshift({
    timestamp: new Date().toLocaleString(),
    action,
    success,
    result,
  });
};

const testJsConfig = async () => {
  loading.value.jsConfig = true;
  errors.value.jsConfig = "";
  results.value.jsConfig = null;

  try {
    const url = testUrl.value || currentUrl.value;
    addLog("info", "开始测试 JS-Config API", { url });
    const result = await getWechatJsConfig(url);
    addLog("info", "JS-Config API 成功", result);
    results.value.jsConfig = result;
    addToHistory("JS-Config API", true, result);
  } catch (error: any) {
    addLog("error", "JS-Config API 错误", error);
    const errorDetails = {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
      stack: error.stack,
      fullError: error,
    };
    errors.value.jsConfig = JSON.stringify(errorDetails, null, 2);
    addToHistory("JS-Config API", false, errorDetails);
  } finally {
    loading.value.jsConfig = false;
  }
};

const testWechatSDKInit = async () => {
  loading.value.sdkInit = true;
  errors.value.sdkInit = "";
  results.value.sdkInit = null;

  try {
    console.log("[WechatDebug] 测试微信SDK初始化 (Debug模式)");

    // 获取环境信息
    const envInfo = wechatService.getEnvironmentInfo();
    console.log("[WechatDebug] 微信环境信息:", envInfo);

    // 初始化SDK (启用debug模式)
    const sdkReady = await wechatService.initializeSDK({
      debug: true, // 强制启用debug模式
    });

    const sdkStatus = wechatService.getSDKStatus();

    const result = {
      envInfo,
      sdkReady,
      sdkStatus,
      timestamp: new Date().toISOString(),
    };

    console.log("[WechatDebug] 微信SDK初始化成功:", result);
    results.value.sdkInit = result;
    addToHistory("微信SDK初始化(Debug)", true, result);
  } catch (error: any) {
    console.error("[WechatDebug] 微信SDK初始化错误:", error);
    const errorDetails = {
      message: error.message,
      stack: error.stack,
      envInfo: wechatService.getEnvironmentInfo(),
      sdkStatus: wechatService.getSDKStatus(),
      fullError: error,
    };
    errors.value.sdkInit = JSON.stringify(errorDetails, null, 2);
    addToHistory("微信SDK初始化(Debug)", false, errorDetails);
  } finally {
    loading.value.sdkInit = false;
  }
};

const testWechatLogin = async () => {
  if (!wechatCode.value.trim()) {
    errors.value.login = "请输入微信授权码";
    return;
  }

  loading.value.login = true;
  errors.value.login = "";
  results.value.login = null;

  try {
    console.log("[WechatDebug] 测试微信登录, code:", wechatCode.value);
    const result = await wechatLogin(wechatCode.value);
    console.log("[WechatDebug] 微信登录成功:", result);
    results.value.login = result;
    if (result.openid) {
      openid.value = result.openid;
    }
    addToHistory("微信登录", true, result);
  } catch (error: any) {
    console.error("[WechatDebug] 微信登录错误:", error);
    const errorDetails = {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
      stack: error.stack,
      fullError: error,
    };
    errors.value.login = JSON.stringify(errorDetails, null, 2);
    addToHistory("微信登录", false, errorDetails);
  } finally {
    loading.value.login = false;
  }
};

const testBindAccount = async () => {
  if (!openid.value.trim() || !userid.value.trim()) {
    errors.value.bind = "请输入OpenID和用户ID";
    return;
  }

  loading.value.bind = true;
  errors.value.bind = "";
  results.value.bind = null;

  try {
    console.log(
      "[WechatDebug] 测试绑定账号, openid:",
      openid.value,
      "userid:",
      userid.value,
    );
    const result = await bindWechatOpenid(openid.value, userid.value);
    console.log("[WechatDebug] 绑定账号成功:", result);
    results.value.bind = result;
    addToHistory("绑定账号", true, result);
  } catch (error: any) {
    console.error("[WechatDebug] 绑定账号错误:", error);
    const errorDetails = {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
      stack: error.stack,
      fullError: error,
    };
    errors.value.bind = JSON.stringify(errorDetails, null, 2);
    addToHistory("绑定账号", false, errorDetails);
  } finally {
    loading.value.bind = false;
  }
};

const testGetBinding = async () => {
  if (!openid.value.trim()) {
    errors.value.getBinding = "请输入OpenID";
    return;
  }

  loading.value.getBinding = true;
  errors.value.getBinding = "";
  results.value.getBinding = null;

  try {
    console.log("[WechatDebug] 测试查询绑定信息, openid:", openid.value);
    const result = await getWechatBinding(openid.value);
    console.log("[WechatDebug] 查询绑定信息成功:", result);
    results.value.getBinding = result;
    if (result.userid) {
      userid.value = result.userid;
    }
    addToHistory("查询绑定信息", true, result);
  } catch (error: any) {
    console.error("[WechatDebug] 查询绑定信息错误:", error);
    const errorDetails = {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
      stack: error.stack,
      fullError: error,
    };
    errors.value.getBinding = JSON.stringify(errorDetails, null, 2);
    addToHistory("查询绑定信息", false, errorDetails);
  } finally {
    loading.value.getBinding = false;
  }
};

const testUpdateBinding = async () => {
  if (!openid.value.trim() || !userid.value.trim()) {
    errors.value.updateBinding = "请输入OpenID和新的用户ID";
    return;
  }

  loading.value.updateBinding = true;
  errors.value.updateBinding = "";
  results.value.updateBinding = null;

  try {
    console.log(
      "[WechatDebug] 测试更新绑定, openid:",
      openid.value,
      "userid:",
      userid.value,
    );
    const result = await updateWechatBinding(openid.value, userid.value);
    console.log("[WechatDebug] 更新绑定成功:", result);
    results.value.updateBinding = result;
    addToHistory("更新绑定", true, result);
  } catch (error: any) {
    console.error("[WechatDebug] 更新绑定错误:", error);
    const errorDetails = {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
      stack: error.stack,
      fullError: error,
    };
    errors.value.updateBinding = JSON.stringify(errorDetails, null, 2);
    addToHistory("更新绑定", false, errorDetails);
  } finally {
    loading.value.updateBinding = false;
  }
};

const generateAuthUrl = () => {
  if (!authConfig.value.appid.trim() || !authConfig.value.redirectUri.trim()) {
    notificationService.error("debug.enterAppIdAndCallback");
    return;
  }

  try {
    console.log("[WechatDebug] 生成授权URL, config:", authConfig.value);
    const url = buildWechatAuthUrl(
      authConfig.value.appid,
      authConfig.value.redirectUri,
      authConfig.value.scope,
      authConfig.value.state || undefined,
    );
    console.log("[WechatDebug] 生成授权URL成功:", url);
    generatedAuthUrl.value = url;
    addToHistory("生成授权URL", true, { url });
  } catch (error: any) {
    console.error("[WechatDebug] 生成授权URL错误:", error);
    const errorDetails = {
      message: error.message,
      stack: error.stack,
      config: authConfig.value,
    };
    addToHistory("生成授权URL", false, errorDetails);
  }
};

const copyToClipboard = async (text: string) => {
  try {
    await navigator.clipboard.writeText(text);
    notificationService.success("debug.copiedToClipboard");
  } catch (error) {
    console.error("复制失败:", error);
    notificationService.error("debug.copyFailed");
  }
};

const clearLogs = () => {
  debugLogs.value = [];
  addLog("info", "日志已清空");
};

const printDebugInfo = () => {
  addLog("info", "打印调试信息");
  addLog("info", "当前环境信息", {
    isWechatEnv: isWechatEnv.value,
    isWechatDevtools: isWechatDevtools.value,
    userAgent: userAgent.value,
    currentUrl: currentUrl.value,
    signatureUrl: signatureUrl.value,
  });

  if (wechatService.isAvailable()) {
    const envInfo = wechatService.getEnvironmentInfo();
    const sdkStatus = wechatService.getSDKStatus();
    addLog("info", "微信服务状态", { envInfo, sdkStatus });
  } else {
    addLog("warn", "微信服务不可用");
  }
};

const clearHistory = () => {
  testHistory.value = [];
};

const exportHistory = () => {
  const data = JSON.stringify(testHistory.value, null, 2);
  const blob = new Blob([data], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `wechat-debug-history-${Date.now()}.json`;
  a.click();
  URL.revokeObjectURL(url);
};

// 自动化授权流程
const startAutomatedAuth = () => {
  if (!authConfig.value.appid.trim()) {
    notificationService.error("debug.configureAppId");
    return;
  }

  authInProgress.value = true;
  authStep.value = "generating";

  try {
    // 生成唯一的状态参数
    const state = `debug_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
    authConfig.value.state = state;

    // 生成授权URL
    const url = buildWechatAuthUrl(
      authConfig.value.appid,
      authConfig.value.redirectUri,
      authConfig.value.scope,
      state,
    );

    generatedAuthUrl.value = url;
    authStep.value = "waiting";

    addLog("info", "开始自动化授权流程");
    addLog("info", "生成授权URL", { url, state });

    // 跳转到授权页面
    window.location.href = url;
  } catch (error: any) {
    authInProgress.value = false;
    authStep.value = "idle";
    addLog("error", "自动化授权流程失败", error);
    notificationService.error("debug.authFlowFailed", {
      params: { message: error.message },
    });
  }
};

// 检查并处理URL参数
const checkUrlParams = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const code = urlParams.get("code");
  const state = urlParams.get("state");
  const error = urlParams.get("error");

  if (error) {
    addLog("error", "授权失败", {
      error,
      error_description: urlParams.get("error_description"),
    });
    authInProgress.value = false;
    authStep.value = "idle";
    return;
  }

  if (code) {
    addLog("info", "检测到授权码", { code, state });

    // 验证state参数
    if (state && state.startsWith("debug_")) {
      authStep.value = "processing";
      wechatCode.value = code;

      // 自动执行登录测试
      setTimeout(() => {
        testWechatLogin()
          .then(() => {
            authStep.value = "completed";
            authInProgress.value = false;

            // 清理URL参数
            const newUrl = window.location.origin + window.location.pathname;
            window.history.replaceState({}, document.title, newUrl);

            addLog("info", "自动化授权流程完成");
          })
          .catch(() => {
            authInProgress.value = false;
            authStep.value = "idle";
          });
      }, 1000);
    } else {
      // 手动填入授权码
      wechatCode.value = code;
      addLog("info", "已自动填入授权码，请手动测试登录");
    }
  }
};

// 获取授权流程状态描述
const getAuthStepDescription = () => {
  switch (authStep.value) {
    case "idle":
      return "等待开始";
    case "generating":
      return "生成授权URL中...";
    case "waiting":
      return "等待用户授权...";
    case "processing":
      return "处理授权结果中...";
    case "completed":
      return "授权流程完成";
    default:
      return "未知状态";
  }
};

const initializePageSpy = () => {
  // 检查是否启用 PageSpy
  const isPageSpyEnabled = import.meta.env.VITE_ENABLE_PAGESPY === "true";

  if (!isPageSpyEnabled) {
    addLog("info", "PageSpy 调试工具已禁用 (VITE_ENABLE_PAGESPY=false)");
    return;
  }

  addLog("info", "开始初始化 PageSpy 调试工具");

  try {
    const harbor = new (window as any).DataHarborPlugin();
    const rrweb = new (window as any).RRWebPlugin();
    (window as any).PageSpy.registerPlugin(harbor);
    (window as any).PageSpy.registerPlugin(rrweb);
    (window as any).$pageSpy = new (window as any).PageSpy({
      // 开启数据序列化，优化传输效率
      serializeData: true,
      project: "vue-wechat-debug",
      title: "微信调试页面",
    });

    pageSpyLoaded.value = true;
    addLog("info", "PageSpy 初始化成功，可在左下角查看调试面板");
    addLog("info", "PageSpy 配置", {
      project: "vue-wechat-debug",
      title: "微信调试页面",
      features: [
        "Console日志",
        "网络请求",
        "页面信息",
        "存储数据",
        "错误监控",
        "用户操作录制",
      ],
    });
  } catch (error) {
    addLog("error", "PageSpy 初始化失败", error);
  }
};

onMounted(() => {
  isWechatEnv.value = isWechatEnvironment();
  isWechatDevtools.value = checkWechatDevtools();
  userAgent.value = navigator.userAgent;
  currentUrl.value = window.location.href;
  signatureUrl.value = getSignatureUrl();
  testUrl.value = currentUrl.value;
  authConfig.value.redirectUri = window.location.href.split("?")[0];

  // 添加初始化日志
  addLog("info", "微信调试页面已加载");
  addLog("info", "环境检测完成", {
    isWechatEnv: isWechatEnv.value,
    isWechatDevtools: isWechatDevtools.value,
    userAgent: userAgent.value,
  });

  // 检查URL参数并处理授权回调
  checkUrlParams();

  // 初始化 PageSpy
  initializePageSpy();

  // 重写console方法以捕获日志
  const originalLog = console.log;
  const originalWarn = console.warn;
  const originalError = console.error;

  console.log = (...args) => {
    if (
      args[0] &&
      typeof args[0] === "string" &&
      args[0].includes("[WechatDebug]")
    ) {
      addLog("info", args[0], args.slice(1));
    }
    originalLog.apply(console, args);
  };

  console.warn = (...args) => {
    if (
      args[0] &&
      typeof args[0] === "string" &&
      args[0].includes("[WechatDebug]")
    ) {
      addLog("warn", args[0], args.slice(1));
    }
    originalWarn.apply(console, args);
  };

  console.error = (...args) => {
    if (
      args[0] &&
      typeof args[0] === "string" &&
      args[0].includes("[WechatDebug]")
    ) {
      addLog("error", args[0], args.slice(1));
    }
    originalError.apply(console, args);
  };
});
</script>

<style scoped>
.wechat-debug-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
  font-family: "Arial", sans-serif;
}

.debug-header {
  text-align: center;
  margin-bottom: 30px;
}

.debug-header h1 {
  color: #1890ff;
  margin-bottom: 10px;
}

.pagespy-info {
  margin-top: 15px;
  padding: 12px;
  background: #f0f9ff;
  border: 1px solid #bae6fd;
  border-radius: 6px;
}

.pagespy-status {
  display: flex;
  align-items: center;
  margin: 0 0 8px 0;
  font-weight: 500;
}

.status-indicator {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #ef4444;
  margin-right: 8px;
  animation: pulse 2s infinite;
}

.status-indicator.active {
  background: #22c55e;
  animation: none;
}

.status-indicator.disabled {
  background: #9ca3af;
  animation: none;
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

.pagespy-tip {
  margin: 0;
  font-size: 14px;
  color: #0369a1;
}

.pagespy-tip.warning {
  color: #ea580c;
}

.debug-section {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
  border: 1px solid #e9ecef;
}

.debug-section h2 {
  color: #333;
  margin-bottom: 15px;
  border-bottom: 2px solid #1890ff;
  padding-bottom: 5px;
}

.env-info {
  display: grid;
  gap: 10px;
}

.info-item {
  display: flex;
  align-items: center;
  gap: 10px;
}

.info-item label {
  font-weight: bold;
  min-width: 120px;
}

.success {
  color: #52c41a;
}
.warning {
  color: #faad14;
}
.info {
  color: #1890ff;
}
.error {
  color: #ff4d4f;
}

.code {
  font-family: "Courier New", monospace;
  background: #f1f1f1;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 12px;
  word-break: break-all;
}

.test-controls {
  display: flex;
  flex-direction: column;
  gap: 15px;
  margin-bottom: 20px;
}

.input-group {
  display: flex;
  align-items: center;
  gap: 10px;
}

.input-group label {
  font-weight: bold;
  min-width: 100px;
}

.input-group input,
.input-group select {
  flex: 1;
  padding: 8px 12px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  font-size: 14px;
}

.button-group {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.test-btn {
  background: #1890ff;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.3s;
}

.test-btn:hover:not(:disabled) {
  background: #40a9ff;
}

.test-btn:disabled {
  background: #d9d9d9;
  cursor: not-allowed;
}

.clear-btn {
  background: #ff4d4f;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
}

.export-btn {
  background: #52c41a;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
}

.copy-btn {
  background: #722ed1;
  color: white;
  border: none;
  padding: 4px 8px;
  border-radius: 4px;
  cursor: pointer;
  margin-left: 10px;
}

.test-link {
  background: #fa8c16;
  color: white;
  text-decoration: none;
  padding: 4px 8px;
  border-radius: 4px;
  margin-left: 10px;
}

.result-box {
  background: #f6ffed;
  border: 1px solid #b7eb8f;
  border-radius: 4px;
  padding: 15px;
  margin-top: 15px;
}

.result-box h3 {
  color: #389e0d;
  margin-bottom: 10px;
}

.error-box {
  background: #fff2f0;
  border: 1px solid #ffccc7;
  border-radius: 4px;
  padding: 15px;
  margin-top: 15px;
}

.error-box h3 {
  color: #cf1322;
  margin-bottom: 10px;
}

.result-box pre,
.error-box pre {
  background: rgba(0, 0, 0, 0.05);
  padding: 10px;
  border-radius: 4px;
  overflow-x: auto;
  white-space: pre-wrap;
  word-wrap: break-word;
  font-size: 12px;
  margin: 0;
}

.url-result {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.url-result .code {
  flex: 1;
  min-width: 200px;
}

.history-controls {
  display: flex;
  gap: 10px;
  margin-bottom: 15px;
}

.history-list {
  max-height: 400px;
  overflow-y: auto;
}

.history-item {
  background: white;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  margin-bottom: 10px;
  padding: 15px;
}

.history-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  font-weight: bold;
}

.timestamp {
  color: #666;
  font-size: 12px;
}

.action {
  color: #333;
}

.status.success {
  color: #52c41a;
}

.status.error {
  color: #ff4d4f;
}

.history-details pre {
  background: #f8f9fa;
  padding: 10px;
  border-radius: 4px;
  overflow-x: auto;
  font-size: 11px;
  max-height: 200px;
  overflow-y: auto;
}

.no-history {
  text-align: center;
  color: #999;
  padding: 20px;
}

.log-container {
  max-height: 400px;
  overflow-y: auto;
  background: #1e1e1e;
  border-radius: 4px;
  padding: 10px;
  font-family: "Courier New", monospace;
  font-size: 12px;
}

.log-item {
  margin-bottom: 8px;
  padding: 4px 0;
  border-bottom: 1px solid #333;
}

.log-item:last-child {
  border-bottom: none;
}

.log-item.info {
  color: #61dafb;
}

.log-item.warn {
  color: #ffc107;
}

.log-item.error {
  color: #f56565;
}

.log-time {
  color: #888;
  margin-right: 8px;
}

.log-level {
  font-weight: bold;
  margin-right: 8px;
}

.log-message {
  margin-right: 8px;
}

.log-data {
  background: rgba(255, 255, 255, 0.1);
  padding: 8px;
  border-radius: 4px;
  margin-top: 4px;
  overflow-x: auto;
  white-space: pre-wrap;
  font-size: 11px;
}

.auth-status {
  background: #e6f7ff;
  border: 1px solid #91d5ff;
  border-radius: 6px;
  padding: 12px;
  margin-bottom: 15px;
}

.status-indicator-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #d9d9d9;
  animation: pulse 2s infinite;
}

.status-dot.active {
  background: #1890ff;
  animation: pulse 2s infinite;
}

.status-text {
  font-weight: 500;
  color: #1890ff;
}

.test-btn.primary {
  background: #52c41a;
  font-weight: bold;
}

.test-btn.primary:hover:not(:disabled) {
  background: #73d13d;
}

.auth-flow-help {
  background: #f6ffed;
  border: 1px solid #b7eb8f;
  border-radius: 6px;
  padding: 15px;
  margin-top: 15px;
}

.auth-flow-help h4 {
  margin: 0 0 10px 0;
  color: #389e0d;
}

.auth-flow-help ol {
  margin: 10px 0;
  padding-left: 20px;
}

.auth-flow-help li {
  margin-bottom: 5px;
  color: #333;
}

.auth-flow-help .tip {
  margin: 10px 0 0 0;
  padding: 8px;
  background: rgba(82, 196, 26, 0.1);
  border-radius: 4px;
  font-size: 14px;
  color: #389e0d;
}

.auth-flow-help code {
  background: #f1f1f1;
  padding: 2px 4px;
  border-radius: 3px;
  font-family: "Courier New", monospace;
  font-size: 12px;
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.7;
    transform: scale(1.1);
  }
}

@media (max-width: 768px) {
  .wechat-debug-container {
    padding: 10px;
  }

  .input-group {
    flex-direction: column;
    align-items: stretch;
  }

  .input-group label {
    min-width: auto;
  }

  .button-group {
    justify-content: center;
  }

  .url-result {
    flex-direction: column;
  }

  .history-header {
    flex-direction: column;
    align-items: stretch;
    gap: 5px;
  }
}
</style>
